import { useEffect } from 'react';
import './App.css';
import { ThemeProvider } from './components/ui/theme-provider';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from 'sonner';
import { UnifiedPermissionsProvider } from './contexts/unified-permissions';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from './components/error/ErrorBoundary';
import { BrowserRouter as Router } from 'react-router-dom';
import { AppRouter } from './routes/AppRouter';
import { TouchGestureProvider } from './hooks/use-touch-gestures';
import { PermissionNotificationDisplay } from '@/components/permissions/PermissionNotificationDisplay';
import { setVh } from '@/utils/setVh';
import { OfflineStateProvider } from '@/contexts/offline';
import { OfflineUIProvider } from '@/contexts/offline/components/OfflineUIComponents';
import { useDeepLinkInitialization } from '@/hooks/useDeepLinkHandler';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Initialize deep link handling
  useDeepLinkInitialization();

  // Set correct vh on mount + resize
  useEffect(() => {
    setVh();

    const handleResize = () => setVh();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Add IndexedDB test utility to window for debugging
    if (process.env.NODE_ENV === 'development') {
      import('@/utils/testIndexedDB').then(({ testIndexedDBInitialization }) => {
        (window as unknown as { testIndexedDB: typeof testIndexedDBInitialization }).testIndexedDB = testIndexedDBInitialization;
      });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);



  return (
    <HelmetProvider>
      <ThemeProvider defaultTheme="light" storageKey="ui-theme">
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary>
            <Router>
              <TouchGestureProvider>
                <OfflineStateProvider>
                  <OfflineUIProvider>
                    <UnifiedPermissionsProvider>
                      <AppRouter />
                      <PermissionNotificationDisplay />
                      <Toaster />
                      <SonnerToaster position="top-right" closeButton />
                    </UnifiedPermissionsProvider>
                  </OfflineUIProvider>
                </OfflineStateProvider>
              </TouchGestureProvider>
            </Router>
          </ErrorBoundary>
        </QueryClientProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
